import * as React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Columns3, MapPin, Clock, Archive, ArchiveRestore, LayoutGrid } from "lucide-react";

import { api, formatApiErrorDetail } from "@/lib/api";
import { useCompany } from "@/context/CompanyContext";
import NewBoardDialog from "@/components/boards/NewBoardDialog";
import { registerCommandProvider } from "@/components/CommandPalette";
import { SkeletonCard } from "@/components/feel/Skeleton";
import { EmptyState } from "@/components/feel/EmptyState";

/**
 * BoardsIndex — dense list of all boards for the current company.
 * Two-column grid, mono card counts, violet accents. New-board gated by
 * boards.manage.
 */
export default function BoardsIndex() {
  const { companyId } = useParams();
  const nav = useNavigate();
  const { has, entitlements } = useCompany();
  const canManage = has("boards.manage");
  const moduleOn = !!entitlements?.modules?.boards;

  const [boards, setBoards] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [showArchived, setShowArchived] = React.useState(false);
  const [newOpen, setNewOpen] = React.useState(false);

  const load = React.useCallback(async () => {
    if (!companyId || !moduleOn) { setLoading(false); return; }
    setLoading(true);
    try {
      const { data } = await api.get(`/companies/${companyId}/boards`, {
        params: showArchived ? { archived: true } : undefined,
      });
      setBoards(data.boards || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not load boards");
    } finally {
      setLoading(false);
    }
  }, [companyId, moduleOn, showArchived]);

  React.useEffect(() => { load(); }, [load]);

  // Cmd+K: jump-to-board
  React.useEffect(() => {
    if (!moduleOn) return () => {};
    const un = registerCommandProvider(() => (boards || []).map((b) => ({
      id: `board-${b.id}`,
      section: "Boards",
      label: b.name,
      hint: b.description || undefined,
      onSelect: () => nav(`/c/${companyId}/m/boards/${b.id}`),
    })));
    return un;
  }, [boards, companyId, moduleOn, nav]);

  // Live: react to board created/updated events
  React.useEffect(() => {
    const onEvt = (e) => {
      const evt = e.detail;
      if (evt?.module !== "boards") return;
      if (["board.created", "board.updated"].includes(evt.type)) load();
    };
    window.addEventListener("fable:event", onEvt);
    return () => window.removeEventListener("fable:event", onEvt);
  }, [load]);

  if (!moduleOn) {
    return (
      <div className="px-4 md:px-6 py-4 md:py-6 text-[13px] text-brand-muted" data-testid="boards-disabled">
        The Boards module is disabled for this company.
      </div>
    );
  }

  return (
    <div className="px-4 md:px-6 py-4 md:py-6 max-w-[1200px]" data-testid="boards-index">
      <header className="flex items-end justify-between">
        <div>
          <div className="micro-label">Module</div>
          <h1 className="mt-1 text-[22px] font-bold tracking-tight text-brand-ink">Boards</h1>
          <div className="text-[12px] text-brand-muted">Kanban, tickets, ops queues.</div>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setShowArchived((v) => !v)}
            className="btn-ghost"
            data-testid="boards-toggle-archived"
          >
            {showArchived ? <><ArchiveRestore size={13} /> Show active</> : <><Archive size={13} /> Show archived</>}
          </button>
          {canManage && (
            <button type="button" className="btn-violet" onClick={() => setNewOpen(true)}
                    data-testid="boards-new">
              <Plus size={13} /> New board
            </button>
          )}
        </div>
      </header>

      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {loading ? (
          <>
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
          </>
        ) : boards.length === 0 ? (
          <div className="col-span-full">
            <EmptyState
              icon={LayoutGrid}
              eyebrow={showArchived ? "Archive" : "Boards"}
              title={showArchived ? "No archived boards." : "No boards yet."}
              body={showArchived ? "Boards you archive will show up here." : "Create your first board to track work in lanes."}
              action={canManage && !showArchived
                ? <button className="btn-violet" onClick={() => setNewOpen(true)} data-testid="boards-empty-new">
                    <Plus size={13} /> New board
                  </button>
                : null}
              testid="boards-empty"
            />
          </div>
        ) : (
          boards.map((b) => (
            <button
              key={b.id}
              type="button"
              onClick={() => nav(`/c/${companyId}/m/boards/${b.id}`)}
              className="card-panel text-left hover:shadow-card transition-shadow"
              data-testid={`board-tile-${b.id}`}
            >
              <div className="card-panel-body">
                <div className="flex items-center gap-2">
                  <div className="h-5 w-5 rounded-md" style={{ background: b.color || "#6E3CFA" }} />
                  <div className="min-w-0 flex-1 text-[14px] font-semibold text-brand-ink truncate">
                    {b.name}
                  </div>
                  {b.archived && <span className="chip chip-amber mono-num text-[10px]">archived</span>}
                </div>
                <div className="mt-1 text-[12px] text-brand-muted line-clamp-2 min-h-[32px]">
                  {b.description || <span className="text-brand-faint">No description.</span>}
                </div>
                <div className="mt-3 flex items-center justify-between text-[11.5px]">
                  <span className="inline-flex items-center gap-1 text-brand-muted">
                    <Columns3 size={11} /> <span className="mono-num text-brand-ink">{b.card_count ?? 0}</span> cards
                  </span>
                  {b.site_id ? (
                    <span className="inline-flex items-center gap-1 text-brand-muted">
                      <MapPin size={11} /> site
                    </span>
                  ) : (
                    <span className="text-brand-faint">Company-wide</span>
                  )}
                </div>
              </div>
            </button>
          ))
        )}
      </div>

      <NewBoardDialog
        open={newOpen}
        onOpenChange={setNewOpen}
        companyId={companyId}
        onCreated={(b) => nav(`/c/${companyId}/m/boards/${b.id}`)}
      />
    </div>
  );
}
